import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowUpRight, Menu, X } from "lucide-react";
import { Link } from "react-router-dom";

const links = [
  { href: "#constat", label: "Constat" },
  { href: "#programme", label: "Programme" },
  { href: "#tarif", label: "Tarif" },
  { href: "#faq", label: "FAQ" },
];

export function scrollToOffer(e) {
  e.preventDefault();
  const card = document.querySelector('[data-testid="pricing-card"]');
  const title = document.querySelector('[data-testid="pricing-title"]');
  if (!card || !title) return;
  const titleWrap = title.closest("div");
  if (titleWrap) {
    titleWrap.style.transform = "none";
    titleWrap.style.opacity = "1";
  }
  card.style.transform = "none";
  card.style.opacity = "1";
  const headerH = 64;
  const groupTop = title.getBoundingClientRect().top + window.scrollY;
  const groupBottom = card.getBoundingClientRect().bottom + window.scrollY;
  const groupH = groupBottom - groupTop;
  const visibleH = window.innerHeight - headerH;
  const cible =
    groupH < visibleH
      ? groupTop - headerH - (visibleH - groupH) / 2
      : groupTop - headerH - 24;
  window.scrollTo({ top: cible, behavior: "smooth" });
}

export default function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  return (
    <>
      <header
        data-testid="main-nav"
        className={`fixed top-0 inset-x-0 z-40 transition-[background-color,border-color] duration-500 ${
          scrolled ? "bg-[#06120E]/80 backdrop-blur-xl border-b border-white/10" : "border-b border-transparent"
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12 h-16 flex items-center justify-between">
          <Link to="/" data-testid="nav-logo" className="flex items-center gap-3 group">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <circle cx="12" cy="12" r="10" stroke="#D92D20" strokeWidth="1.4" />
              <ellipse cx="12" cy="12" rx="4.5" ry="10" stroke="#FF6B00" strokeWidth="1" />
              <line x1="2" y1="12" x2="22" y2="12" stroke="#FF6B00" strokeWidth="1" />
            </svg>
            <span className="font-mono text-xs tracking-[0.3em] text-[#F5F5F0] group-hover:text-white transition-colors">
              AC&nbsp;INVESTING
            </span>
          </Link>
          <nav className="hidden lg:flex items-center gap-8" aria-label="Navigation principale">
            {links.map((l) => (
              <a
                key={l.href}
                href={l.href}
                data-testid={`nav-link-${l.label.toLowerCase()}`}
                className="font-mono text-[11px] tracking-[0.2em] uppercase text-[#A3B8B0] hover:text-[#F5F5F0] transition-colors"
              >
                {l.label}
              </a>
            ))}
          </nav>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={scrollToOffer}
              data-testid="nav-cta-button"
              className="flex items-center gap-2 bg-[#D92D20] hover:bg-[#b3241a] text-white font-mono text-[11px] tracking-[0.2em] uppercase px-4 py-2.5 rounded-sm transition-[background-color,transform] duration-300 hover:scale-[1.02]"
            >
              Accéder
              <ArrowUpRight size={14} />
            </button>
            <button
              type="button"
              onClick={() => setOpen(true)}
              data-testid="nav-menu-button"
              aria-label="Ouvrir le menu"
              className="md:hidden flex items-center justify-center w-10 h-10 border border-white/20 rounded-sm text-[#F5F5F0] hover:bg-white/5 transition-colors"
            >
              <Menu size={18} />
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="fixed inset-0 z-50 bg-[#06120E] flex flex-col md:hidden"
            data-testid="mobile-menu"
          >
            <div className="h-16 px-6 flex items-center justify-between border-b border-white/10">
              <span className="font-mono text-xs tracking-[0.3em] text-[#F5F5F0]">AC&nbsp;INVESTING</span>
              <button
                type="button"
                onClick={() => setOpen(false)}
                data-testid="nav-menu-close"
                aria-label="Fermer le menu"
                className="flex items-center justify-center w-10 h-10 border border-white/20 rounded-sm text-[#F5F5F0] hover:bg-white/5 transition-colors"
              >
                <X size={18} />
              </button>
            </div>
            <nav className="flex-1 flex flex-col items-center justify-center gap-10" aria-label="Navigation mobile">
              {links.map((l, i) => (
                <motion.a
                  key={l.href}
                  href={l.href}
                  onClick={() => setOpen(false)}
                  data-testid={`nav-mobile-link-${l.label.toLowerCase()}`}
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.08 * i + 0.1, duration: 0.4 }}
                  className="font-mono text-lg tracking-[0.3em] uppercase text-[#F5F5F0] hover:text-[#FF6B00] transition-colors"
                >
                  {l.label}
                </motion.a>
              ))}
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
